import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ClipPlayer from "@/components/ClipPlayer";
import YSVLogo from "@/components/YSVLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedHighlightPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);
  const [viewFired, setViewFired] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/highlight/${id}`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.status === 404 ? "Highlight not found" : "Failed to load highlight"));
  }, [id]);

  // Fire a single "view" event once the banner is actually rendered to the user.
  // Idempotent per visit thanks to the viewFired flag.
  useEffect(() => {
    if (data?.promo_banner_url && !viewFired) {
      setViewFired(true);
      axios.post(`${API}/public/promo-banner/event`, { kind: "view", highlight_id: id }).catch(() => {});
    }
  }, [data, viewFired, id]);

  const recordBannerClick = () => {
    axios.post(`${API}/public/promo-banner/event`, { kind: "click", highlight_id: id }).catch(() => {});
  };

  if (error) {
    return (
      <div data-testid="shared-highlight-error" style={{ minHeight: '100vh', background: '#0a0a0c', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center' }}>
          <YSVLogo size="sm" />
          <p style={{ color: '#f44336', marginTop: 24, fontSize: 14 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading highlight…</p>
      </div>
    );
  }

  if (started && data.clips.length > 0) {
    return (
      <ClipPlayer
        clips={data.clips}
        title={data.highlight.title}
        subtitle={`Highlight tape · Shared by ${data.highlight.created_by_name || "YSV"}`}
        onBack={() => setStarted(false)}
      />
    );
  }

  return (
    <div data-testid="shared-highlight-page" style={{ minHeight: '100vh', background: '#0a0a0c', padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 'min(96vw, 560px)' }}>
        {/* Large centered YSV logo at the top */}
        <div style={{ textAlign: 'center', marginBottom: 28, display: 'flex', justifyContent: 'center' }}>
          <YSVLogo size="xxl" />
        </div>

        <div className="glass-card" style={{ padding: 32, textAlign: 'center' }}>
          <h1 data-testid="shared-highlight-title" style={{
            fontSize: 26, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
            marginBottom: 8, color: '#fff',
          }}>{data.highlight.title}</h1>
          {data.highlight.description && (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 18 }}>{data.highlight.description}</p>
          )}
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 24 }}>
            {data.clips.length} clip{data.clips.length === 1 ? "" : "s"} · shared by {data.highlight.created_by_name || "YSV"}
          </p>

          {data.clips.length === 0 ? (
            <p data-testid="shared-highlight-empty" style={{ color: '#ff9800', fontSize: 14 }}>This highlight has no playable clips yet.</p>
          ) : (
            <button
              data-testid="shared-highlight-play"
              onClick={() => setStarted(true)}
              className="btn-primary"
              style={{ width: '100%', padding: '14px 20px', fontSize: 15, fontWeight: 700 }}
            >
              ▶ Play Highlight Tape
            </button>
          )}

          <p style={{ fontSize: 11, color: '#666', marginTop: 24 }}>
            Love this? <a href="/" style={{ color: 'var(--cyan)', textDecoration: 'none', fontWeight: 600 }}>Learn more about YSV →</a>
          </p>
        </div>

        {/* YSV-admin uploaded promo banner — bottom of share landing page */}
        {data.promo_banner_url && (
          <div data-testid="shared-highlight-promo" style={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}>
            <img
              src={data.promo_banner_url}
              alt="Promotion"
              onClick={recordBannerClick}
              style={{
                width: 'min(96vw, 320px)', height: 'min(96vw, 320px)',
                maxWidth: 320, maxHeight: 320,
                objectFit: 'cover', borderRadius: 14,
                boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                cursor: 'pointer',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedHighlightPage;
